import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import "./ManageNurseRequest.css";
import Widgets from "../../../../widgets";
import { useTranslation } from "react-i18next";
import strings from "../../../../utils/strings";
import Icons from "../../../../Assets/Icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getNurseRequests, getNurseSessions, getSessionNurses, assignSessionNurse, rescheduleSession, deleteSession, deleteNursingRequest, fetchSessionById } from "../../../../modules/NursignCare/ManageRequest/manageRequestThunk";
import {
  matchesRequestSearch,
  sortByReceivingTimeDesc,
  sortByQuotationSentTimeDesc,
  sortByPaymentMadeTimeDesc,
  sortByScheduledTimeDesc,
  sortByCheckoutTimeDesc,
  sortByCancelledTimeDesc,
} from "../../../../modules/NursignCare/ManageRequest/requestListUtils";
import ToastPopup from "../../../../widgets/Toast/toastPopup";
import ChatWidget from "../../../Chat/ChatWidget";

// ── Shared TimePicker (mirrors SessionDetailsPage.jsx) ──────────────────────
const to24hMinutes = (h, m, p) => {
  let hrs = parseInt(h, 10);
  if (p === "PM" && hrs !== 12) hrs += 12;
  if (p === "AM" && hrs === 12) hrs = 0;
  return hrs * 60 + parseInt(m, 10);
};

const TimePicker = ({ value, onChange, minMinutes = null }) => {
  const parse = (v) => {
    if (!v) return { h: "10", m: "00", p: "AM" };
    const match = v.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (match) return { h: match[1].padStart(2, "0"), m: match[2], p: match[3].toUpperCase() };
    return { h: "10", m: "00", p: "AM" };
  };
  const { h, m, p } = parse(value);
  const upd = (nh, nm, np) => onChange(`${nh}:${nm} ${np}`);
  const sel = {
    className: "sd-time-select",
    style: {
      border: "1px solid var(--box-border)",
      borderRadius: "0.6rem",
      padding: "0.4rem 0.6rem",
      fontSize: "1.4rem",
      background: "var(--main-bg)",
      cursor: "pointer",
    },
  };
  const isDisabled = (nh, nm, np) =>
    minMinutes !== null && to24hMinutes(nh, nm, np) < minMinutes;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
      <select {...sel} value={h} onChange={(e) => upd(e.target.value, m, p)}>
        {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map((v) => (
          <option key={v} value={v} disabled={isDisabled(v, m, p)}>
            {v}
          </option>
        ))}
      </select>
      <span style={{ fontWeight: 700 }}>:</span>
      <select {...sel} value={m} onChange={(e) => upd(h, e.target.value, p)}>
        {["00", "15", "30", "45"].map((v) => (
          <option key={v} value={v} disabled={isDisabled(h, v, p)}>
            {v}
          </option>
        ))}
      </select>
      <select {...sel} value={p} onChange={(e) => upd(h, m, e.target.value)}>
        <option value="AM" disabled={isDisabled(h, m, "AM")}>AM</option>
        <option value="PM" disabled={isDisabled(h, m, "PM")}>PM</option>
      </select>
    </div>
  );
};
// ────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 10;
const FULL_LIST_LIMIT = 1000;

const TAB_CONFIG = [
  { key: "new", statuses: ["OPEN", "REJECTED", "ASSIGNED"], session: false },
  { key: "approved", statuses: ["VERIFIED"], session: false },
  { key: "scheduled", statuses: ["SCHEDULED"], session: false },
  { key: "upcoming", statuses: ["CONFIRMED", "ASSIGNED", "CHECKED_IN"], filterStatuses: ["ASSIGNED", "CONFIRMED"], session: true },
  { key: "past", statuses: ["CHECKED_OUT", "COMPLETED"], session: true },
  { key: "deletedSessions", statuses: ["CANCELLED"], session: true },
  { key: "deletedRequests", statuses: ["CANCELLED"], session: false },
];

const OBJECT_ID_PATTERN = /^[a-f\d]{24}$/i;

const ManageNurseRequest = ({ scrollToTop }) => {
  const nav = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const breadcrumbs = ["nursingCare", "manageRequests"];
  const paths = [{ path: strings.navigation.manageNurseReq }, strings.navigation.manageNurseReq];
  const [showFilter, setShowFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState(queryParams.get("search") || "");
  const [listPage, setListPage] = useState(Number(queryParams.get("page")) || 1);
  const modalRef = useRef(null);

  // Appointment date range filter (Upcoming tab)
  const [appointmentFromDate, setAppointmentFromDate] = useState(queryParams.get("fromDate") || "");
  const [appointmentToDate, setAppointmentToDate] = useState(queryParams.get("toDate") || "");

  // Nurse assignment modal (upcoming tab)
  const [nurseModal, setNurseModal] = useState({ open: false, sessionId: null, sessionItem: null });
  const [nurseOptions, setNurseOptions] = useState([]);
  const [nurseOptionsLoading, setNurseOptionsLoading] = useState(false);
  // Array of selected nurse options [{ value, label }, ...]
  const [pendingNurseSelections, setPendingNurseSelections] = useState([null]);
  const [nursingInstructions, setNursingInstructions] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [reassignReasonError, setReassignReasonError] = useState(false);
  const [showNurseConfirm, setShowNurseConfirm] = useState(false);
  const [assignedNurses, setAssignedNurses] = useState({});

  // Reschedule modal state
  const [rescheduleModal, setRescheduleModal] = useState({ open: false, sessionId: null });
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleCheckIn, setRescheduleCheckIn] = useState("10:00 AM");
  const [rescheduleCheckOut, setRescheduleCheckOut] = useState("01:00 PM");
  const [rescheduleErrors, setRescheduleErrors] = useState({});

  // Delete session/request modal — kind is "SESSION" or "REQUEST"
  const [deleteModal, setDeleteModal] = useState({ open: false, kind: null, id: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  const activeList = queryParams.get("activeList") || "new";
  const dispatch = useDispatch();

  const { newRequests, approvedRequests, scheduledRequests, upcomingRequests, pastRequests, deletedSessions, deletedRequests, totalDocuments } = useSelector((state) => state.manageRequest);

  const activeTabConfig = TAB_CONFIG.find((c) => c.key === activeList) || TAB_CONFIG[0];
  const statusTypes = activeTabConfig.statuses;

  let statuses = queryParams.getAll("status");
  const [selectedStatuses, setSelectedStatuses] = useState(statuses.length > 0 ? statuses : statusTypes);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const nextSearch = queryParams.get("search") || "";
    const nextPage = Number(queryParams.get("page")) || 1;
    const nextFromDate = queryParams.get("fromDate") || "";
    const nextToDate = queryParams.get("toDate") || "";
    setSearchTerm(nextSearch);
    setListPage(nextPage);
    setAppointmentFromDate(nextFromDate);
    setAppointmentToDate(nextToDate);
  }, [location.search]);

  const updateListQuery = useCallback(
    (updates) => {
      const next = new URLSearchParams(location.search);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === "" || value === null || value === undefined) next.delete(key);
        else next.set(key, String(value));
      });
      const nextSearch = next.toString();
      if (nextSearch !== location.search.replace(/^\?/, "")) {
        nav(`${location.pathname}?${nextSearch}`, { replace: true });
      }
    },
    [location.pathname, location.search, nav],
  );

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      const urlStatuses = queryParams.getAll("status");
      const effective = urlStatuses.length > 0 ? urlStatuses : statusTypes;
      if (!(selectedStatuses?.length === effective.length && selectedStatuses.every((v, i) => v === effective[i]))) {
        setSelectedStatuses(effective);
      }
    }, 10);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [queryParams]);

  useEffect(() => {
    queryParams.delete("status");
    if (selectedStatuses !== null) {
      selectedStatuses?.forEach((status) => queryParams.append("status", status));
      nav(`${location.pathname}?${queryParams.toString()}`);
    }
  }, [selectedStatuses]);

  const getEffectiveStatuses = () => {
    if (selectedStatuses?.length > 0) return selectedStatuses;
    return activeTabConfig.statuses;
  };

  const loadTabData = useCallback(() => {
    const effectiveStatuses = getEffectiveStatuses();
    setListPage(1);
    if (activeTabConfig.session) {
      dispatch(getNurseSessions({ tab: activeList, statuses: effectiveStatuses, page: 1, limit: FULL_LIST_LIMIT }));
    } else {
      dispatch(getNurseRequests({ tab: activeList, statuses: effectiveStatuses, page: 1, limit: FULL_LIST_LIMIT }));
    }
  }, [activeList, activeTabConfig.session, dispatch, selectedStatuses]);

  const isMounted = useRef(false);
  const skipStatusEffect = useRef(false);

  useEffect(() => {
    skipStatusEffect.current = true;
    const statuses = activeTabConfig.statuses;
    setListPage(1);
    if (activeTabConfig.session) {
      dispatch(getNurseSessions({ tab: activeList, statuses, page: 1, limit: FULL_LIST_LIMIT }));
    } else {
      dispatch(getNurseRequests({ tab: activeList, statuses, page: 1, limit: FULL_LIST_LIMIT }));
    }
  }, [activeList]);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (skipStatusEffect.current) {
      skipStatusEffect.current = false;
      return;
    }
    loadTabData();
  }, [selectedStatuses]);

  useEffect(() => {
    setListPage(1);
    updateListQuery({ search: searchTerm, page: 1 });
  }, [searchTerm]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) setShowFilter(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [modalRef]);

  useEffect(() => {
    setListPage(1);
    updateListQuery({ fromDate: appointmentFromDate, toDate: appointmentToDate, page: 1 });
  }, [appointmentFromDate, appointmentToDate]);

  const requestList =
    activeList === "new"
      ? newRequests
      : activeList === "approved"
        ? approvedRequests
        : activeList === "scheduled"
          ? scheduledRequests
          : activeList === "upcoming"
            ? upcomingRequests
            : activeList === "past"
              ? pastRequests
              : activeList === "deletedSessions"
                ? deletedSessions
                : activeList === "deletedRequests"
                  ? deletedRequests
      : [];

  const toLocalDateStr = (rawStartTime) => {
    if (!rawStartTime) return "";
    // startTime may be an ISO string, or a pre-formatted display string like "23 Jun 2026 at 10:00 AM"
    let d = new Date(rawStartTime);
    if (isNaN(d) && typeof rawStartTime === "string") {
      d = new Date(rawStartTime.replace(" at ", " "));
    }
    if (isNaN(d)) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  // Same ISO/pre-formatted string handling as toLocalDateStr, but extracts the
  // "H:MM AM/PM" the TimePicker above expects instead of the date portion.
  const toTimePickerStr = (rawTime) => {
    if (!rawTime) return "";
    let d = new Date(rawTime);
    if (isNaN(d) && typeof rawTime === "string") {
      d = new Date(rawTime.replace(" at ", " "));
    }
    if (isNaN(d)) return "";
    let h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, "0");
    const period = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;
    return `${String(h).padStart(2, "0")}:${m} ${period}`;
  };

  const filteredList = useMemo(() => {
    let sorted;
    if (activeList === "new") {
      sorted = sortByReceivingTimeDesc(requestList);
    } else if (activeList === "approved") {
      sorted = sortByQuotationSentTimeDesc(requestList);
    } else if (activeList === "scheduled") {
      sorted = sortByPaymentMadeTimeDesc(requestList);
    } else if (activeList === "upcoming") {
      sorted = sortByScheduledTimeDesc(requestList);
    } else if (activeList === "past") {
      sorted = sortByCheckoutTimeDesc(requestList);
    } else if (activeList === "deletedSessions" || activeList === "deletedRequests") {
      sorted = sortByCancelledTimeDesc(requestList);
    } else {
      sorted = sortByReceivingTimeDesc(requestList);
    }
    let result = sorted;
    if (activeList === "upcoming" && (appointmentFromDate || appointmentToDate)) {
      result = result.filter((item) => {
        const itemDate = toLocalDateStr(item.startTime);
        if (!itemDate) return true;
        if (appointmentFromDate && itemDate < appointmentFromDate) return false;
        if (appointmentToDate && itemDate > appointmentToDate) return false;
        return true;
      });
    }
    if (!searchTerm.trim()) return result;
    return result.filter((item) => matchesRequestSearch(item, searchTerm));
  }, [requestList, searchTerm, activeList, appointmentFromDate, appointmentToDate]);

  const totalPages = filteredList.length === 0 ? 0 : Math.ceil(filteredList.length / PAGE_SIZE);

  const displayList = useMemo(() => {
    const start = (listPage - 1) * PAGE_SIZE;
    return filteredList.slice(start, start + PAGE_SIZE);
  }, [filteredList, listPage]);

  // The CANCELLED sessions list endpoint doesn't return cancelledAt, only the
  // single-session detail endpoint does — so backfill it per visible row.
  const [cancelledAtById, setCancelledAtById] = useState({});
  useEffect(() => {
    if (activeList !== "deletedSessions") return;
    const missingIds = displayList.map((item) => item._id).filter((id) => id && !(id in cancelledAtById));
    if (missingIds.length === 0) return;
    missingIds.forEach((id) => {
      dispatch(fetchSessionById({ sessionId: id }))
        .unwrap()
        .then((res) => {
          const cancelledAt = res?.data?.session?.cancelledAt ?? res?.session?.cancelledAt ?? null;
          setCancelledAtById((prev) => ({ ...prev, [id]: cancelledAt }));
        })
        .catch(() => {
          setCancelledAtById((prev) => ({ ...prev, [id]: null }));
        });
    });
  }, [activeList, displayList, dispatch, cancelledAtById]);

  const activeTabCount = searchTerm.trim() ? filteredList.length : totalDocuments || requestList.length;

  useEffect(() => {
    if (totalPages > 0 && listPage > totalPages) {
      setListPage(totalPages);
    }
  }, [listPage, totalPages]);

  const handleActiveChange = (section) => {
    const url = `${strings.navigation.manageNurseReq}?activeList=${encodeURIComponent(section)}`;
    nav(url);
  };

  const handleClick = (pageNumber) => {
    scrollToTop();
    setListPage(pageNumber);
    updateListQuery({ page: pageNumber });
  };

  const handlePrevNext = (direction) => {
    scrollToTop();
    if (direction === "prev" && listPage > 1) {
      setListPage(listPage - 1);
      updateListQuery({ page: listPage - 1 });
    }
    if (direction === "next" && listPage < totalPages) {
      setListPage(listPage + 1);
      updateListQuery({ page: listPage + 1 });
    }
  };

  const renderPagination = () => {
    const pages = [];
    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (listPage <= 2) {
        pages.push(1, 2, { type: "ellipsis" }, totalPages);
      } else if (listPage >= totalPages - 1) {
        pages.push(1, { type: "ellipsis" }, totalPages - 1, totalPages);
      } else {
        pages.push(1, { type: "ellipsis" }, listPage, { type: "ellipsis" }, totalPages);
      }
    }
    return pages;
  };

  const resolveLocalized = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object") return val.en || val.vi || Object.values(val).find((v) => typeof v === "string") || "";
    return String(val);
  };
  const nurseToOption = (n) => {
    if (!n) return null;
    const label = `${resolveLocalized(n.firstName)} ${resolveLocalized(n.lastName)}`.trim() || n.employeeID || n._id || "";
    return label ? { value: n._id, label } : null;
  };
  // Returns a display option with all nurse names joined — handles array or single
  const nursesToDisplayOption = (raw) => {
    const arr = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const valid = arr.map(nurseToOption).filter(Boolean);
    if (!valid.length) return null;
    return { value: valid[0].value, label: valid.map((o) => o.label).join(", ") };
  };

  const handleNurseEdit = (e, item) => {
    e.stopPropagation();
    // Seed from already-assigned nurses (may be array) or single
    const raw = item.nurse;
    const existingArray = Array.isArray(raw) && raw.length
      ? raw.map((n) => nurseToOption(n)).filter(Boolean)
      : [assignedNurses[item._id] || nursesToDisplayOption(raw)].filter(Boolean);
    setNurseModal({ open: true, sessionId: item._id, sessionItem: item });
    setPendingNurseSelections(existingArray.length ? existingArray : [null]);
    setNursingInstructions(item.nursingInstructions || item.nursingInstruction || item.instructions || "");
    setReassignReason("");
    setReassignReasonError(false);
    setNurseOptions([]);
    setNurseOptionsLoading(true);
    dispatch(getSessionNurses({ sessionId: item._id })).then((res) => {
      setNurseOptionsLoading(false);
      if (res.meta.requestStatus === "fulfilled") {
        setNurseOptions(
          (res.payload.nurses || []).map((n) => ({
            value: n._id,
            label: `${resolveLocalized(n.firstName)} ${resolveLocalized(n.lastName)}`.trim() || n.employeeID || n._id,
          })),
        );
      }
    });
  };

  const handleCloseNurseModal = () => {
    setNurseModal({ open: false, sessionId: null, sessionItem: null });
    setPendingNurseSelections([null]);
    setNursingInstructions("");
    setReassignReason("");
    setReassignReasonError(false);
  };

  // True when the session already has a nurse assigned (= reassign flow)
  const isReassign = !!nursesToDisplayOption(nurseModal.sessionItem?.nurse);

  // "Save" in nurse selection modal → validate reason if reassigning, then show confirmation popup
  const handleNurseSaveClick = () => {
    const valid = pendingNurseSelections.filter(Boolean);
    if (!valid.length) return;
    if (isReassign && !reassignReason.trim()) {
      setReassignReasonError(true);
      return;
    }
    setReassignReasonError(false);
    setShowNurseConfirm(true);
  };

  // User confirmed → call assign API with all selected nurses
  const handleConfirmNurseAssign = () => {
    const validSelections = pendingNurseSelections.filter(Boolean);
    if (!validSelections.length || !nurseModal.sessionId) {
      setShowNurseConfirm(false);
      setNurseModal({ open: false });
      return;
    }
    const sessionId = nurseModal.sessionId;
    const instructions = nursingInstructions.trim();
    const nurseIDs = validSelections.map((n) => n.value);
    const reason = isReassign ? reassignReason.trim() : undefined;
    setShowNurseConfirm(false);
    setNurseModal({ open: false, sessionId: null, sessionItem: null });

    dispatch(assignSessionNurse({ sessionId, nurseIDs, nursingInstructions: instructions, reason })).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        // Store label as joined names for display
        const label = validSelections.map((n) => n.label).join(", ");
        setAssignedNurses((prev) => ({ ...prev, [sessionId]: { value: nurseIDs[0], label } }));
        setPendingNurseSelections([null]);
        setNursingInstructions("");
        setReassignReason("");
        ToastPopup.success(t("nurseUpdatedSuccess"));
        // Auto-refresh upcoming sessions list
        dispatch(getNurseSessions({ tab: "upcoming", statuses: ["CONFIRMED", "ASSIGNED", "CHECKED_IN"], page: 1, limit: FULL_LIST_LIMIT }));
      } else {
        ToastPopup.error(res.error?.message ? t(res.error.message, { defaultValue: res.error.message }) : t("failedToUpdateNurse"));
      }
    });
  };

  const handleOpenReschedule = (e, item) => {
    e.stopPropagation();
    // Pre-fill with the session's currently scheduled date/time. The "upcoming" list's session
    // objects only carry startTime/endTime (no checkInTime/checkOutTime — those only show up on
    // the single-session detail fetch), so derive the time-picker strings from those instead.
    setRescheduleDate(toLocalDateStr(item.startTime || item.date));
    setRescheduleCheckIn(toTimePickerStr(item.startTime) || item.checkInTime || "10:00 AM");
    setRescheduleCheckOut(toTimePickerStr(item.endTime) || item.checkOutTime || "01:00 PM");
    setRescheduleErrors({});
    setRescheduleModal({ open: true, sessionId: item._id });
  };

  const handleCloseRescheduleModal = () => {
    setRescheduleModal({ open: false, sessionId: null });
    setRescheduleErrors({});
  };

// Parse "HH:MM AM/PM" → minutes since midnight
  const parseTimePickerMinutes = (val) => {
    if (!val) return null;
    const match = val.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return null;
    return to24hMinutes(match[1], match[2], match[3].toUpperCase());
  };

  const validateReschedule = () => {
    const errors = {};
    const today = new Date().toISOString().split("T")[0];
    if (!rescheduleDate) {
      errors.date = t("dateRequired");
    } else if (rescheduleDate < today) {
      errors.date = t("pastDateNotAllowed");
    }

    const checkInMins = parseTimePickerMinutes(rescheduleCheckIn);
    const checkOutMins = parseTimePickerMinutes(rescheduleCheckOut);

    if (checkInMins === null) {
      errors.checkIn = t("checkInRequired");
    } else if (rescheduleDate === today) {
      const nowMins = new Date().getHours() * 60 + new Date().getMinutes();
      if (checkInMins <= nowMins) errors.checkIn = t("pastDateNotAllowed");
    }

    if (checkOutMins === null) {
      errors.checkOut = t("checkOutRequired");
    }

    if (checkInMins !== null && checkOutMins !== null && checkOutMins <= checkInMins) {
      errors.timeRange = t("invalidTimeRange");
    }

    return errors;
  };

// Convert "HH:MM AM/PM" → "HH:mm" 24h for the API
  const timePickerTo24h = (val) => {
    if (!val) return "";
    const match = val.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return val;
    let h = parseInt(match[1], 10);
    const mins = match[2];
    const period = match[3].toUpperCase();
    if (period === "PM" && h !== 12) h += 12;
    if (period === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${mins}`;
  };

  const handleRescheduleConfirm = () => {
    const errors = validateReschedule();
    if (Object.keys(errors).length > 0) {
      setRescheduleErrors(errors);
      return;
    }
    dispatch(rescheduleSession({
      sessionId: rescheduleModal.sessionId,
      body: {
        date: rescheduleDate,
        checkInTime: timePickerTo24h(rescheduleCheckIn),
        checkOutTime: timePickerTo24h(rescheduleCheckOut),
      },
    })).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        ToastPopup.success(t("rescheduleSuccess"));
        handleCloseRescheduleModal();
        loadTabData();
      } else {
        ToastPopup.error(res.error?.message || t("rescheduleFailed"));
      }
    });
  };

  const formatSessionDateTime = (startTime) => {
    if (!startTime) return "-";
    // If already formatted (e.g. "23 Jun 2026 at 10:00 AM"), return as-is
    if (typeof startTime === "string" && !/^\d{4}-\d{2}-\d{2}T/.test(startTime)) return startTime;
    // ISO format → parse and format
    const d = new Date(startTime);
    if (isNaN(d)) return startTime;
    const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    return `${date} at ${time}`;
  };

  const getPatientFromRequest = (item) => {
    return (typeof item.patient === "object" && item.patient) || null;
  };

  const getRequestHn = (item) => {
    const patient = getPatientFromRequest(item);
    return patient?.hnNumber || item.hnNumber || item.hn || "-";
  };

  const formatRequestDateTime = (dateTime) => {
    if (!dateTime) return "";
    if (typeof dateTime === "string" && !/^\d{4}-\d{2}-\d{2}T/.test(dateTime)) return dateTime;

    const d = dateTime instanceof Date ? dateTime : new Date(dateTime);
    if (isNaN(d)) return "";

    const date = d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    const time = d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    return `${date} at ${time}`;
  };

  const getDateFromObjectId = (id) => {
    if (!OBJECT_ID_PATTERN.test(id || "")) return null;
    const timestamp = parseInt(id.slice(0, 8), 16) * 1000;
    const date = new Date(timestamp);
    return isNaN(date) ? null : date;
  };

  const getRequestReceivingTime = (item) => {
    const raw = item.requestReceivingTime || item.receivedAt || item.requestedAt || item.submittedAt || item.createdAt;

    const formattedRaw = formatRequestDateTime(raw);
    if (formattedRaw) return formattedRaw;

    return formatRequestDateTime(getDateFromObjectId(item._id));
  };

  const handleRowClick = (item) => {
    const returnTo = encodeURIComponent(`${location.pathname}${location.search}`);
    switch (activeList) {
      case "approved":
        nav(`${strings.navigation.viewApprovedRequestDetails}?id=${encodeURIComponent(item._id)}&from=approved`, {
          state: {
            patientCondition: item.patientCondition,
            distanceInKm: item.distanceInKm ?? item.zone ?? item.address?.zone,
          },
        });
        break;
      case "scheduled":
        nav(`${strings.navigation.viewSessionDetails}?requestId=${encodeURIComponent(item._id)}&from=scheduled&returnTo=${returnTo}`);
        break;
      case "upcoming":
        nav(`${strings.navigation.viewSessionDetails}?id=${encodeURIComponent(item._id)}&from=upcoming&returnTo=${returnTo}`);
        break;
      case "past":
        nav(`${strings.navigation.viewSessionDetails}?id=${encodeURIComponent(item._id)}&from=past&returnTo=${returnTo}`);
        break;
      case "deletedSessions":
        nav(`${strings.navigation.viewDeletedSessionDetails}?id=${encodeURIComponent(item._id)}&returnTo=${returnTo}`);
        break;
      case "deletedRequests":
        nav(`${strings.navigation.viewDeletedRequestDetails}?id=${encodeURIComponent(item._id)}&returnTo=${returnTo}`);
        break;
      default:
        nav(`${strings.navigation.viewNurseReqDetails}?id=${encodeURIComponent(item._id)}`);
        break;
    }
  };

  const openDeleteModal = (e, kind, id) => {
    e.stopPropagation();
    setDeleteModal({ open: true, kind, id });
  };

  const handleCloseDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteModal({ open: false, kind: null, id: null });
  };

  const handleDeleteSubmit = (reason) => {
    if (deleteLoading || !deleteModal.id) return;
    setDeleteLoading(true);
    const action = deleteModal.kind === "SESSION" ? deleteSession({ sessionId: deleteModal.id, reason }) : deleteNursingRequest({ requestId: deleteModal.id, reason });
    dispatch(action).then((res) => {
      setDeleteLoading(false);
      if (res.meta.requestStatus === "fulfilled") {
        ToastPopup.success(deleteModal.kind === "SESSION" ? t("sessionDeletedSuccess") : t("requestDeletedSuccess"));
        setDeleteModal({ open: false, kind: null, id: null });
        loadTabData();
      } else {
        ToastPopup.error(res.error?.message || t("deleteFailed"));
      }
    });
  };

  const renderTableHead = () => {
    switch (activeList) {
      case "upcoming":
        return (
          <tr>
            <th>{t("patientDetails")}</th>
            <th>{t("hn")}</th>
            <th>{t("appointmentTime") || "Appointment Time"}</th>
            <th>{t("scheduledTime")}</th>
            <th>{t("HomecareStaff")}</th>
            <th>{t("paymentType")}</th>
            <th>{t("status")}</th>
            <th>{t("actions")}</th>
          </tr>
        );
      case "past":
        return (
          <tr>
            <th>{t("patientDetails")}</th>
            <th>{t("hn")}</th>
            <th>{t("sessionDateTime")}</th>
            <th>{t("checkOutTime")}</th>
            <th>{t("HomecareStaff")}</th>
            <th>{t("paymentType")}</th>
            <th>{t("status")}</th>
          </tr>
        );
      case "scheduled":
        return (
          <tr>
            <th>{t("patientDetails")}</th>
            <th>{t("hn")}</th>
            <th>{t("request_id")}</th>
            <th>{t("paymentMadeTime")}</th>
            <th>{t("paymentType")}</th>
            <th>{t("status")}</th>
            <th>{t("actions")}</th>
          </tr>
        );
      case "approved":
        return (
          <tr>
            <th>{t("patientDetails")}</th>
            <th>{t("hn")}</th>
            <th>{t("request_id")}</th>
            <th>{t("quotationSentTime")}</th>
            <th>{t("status")}</th>
            <th>{t("actions")}</th>
          </tr>
        );
      case "deletedSessions":
        return (
          <tr>
            <th>{t("patientDetails")}</th>
            <th>{t("hn")}</th>
            <th>{t("sessionDateTime")}</th>
            <th>{t("cancelledAt")}</th>
            <th>{t("status")}</th>
          </tr>
        );
      case "deletedRequests":
        return (
          <tr>
            <th>{t("patientDetails")}</th>
            <th>{t("hn")}</th>
            <th>{t("request_id")}</th>
            <th>{t("cancelledAt")}</th>
            <th>{t("status")}</th>
          </tr>
        );
      case "new":
      default:
        return (
          <tr>
            <th>{t("patientDetails")}</th>
            <th>{t("hn")}</th>
            <th>{t("request_id")}</th>
            <th>{t("requestReceivingTime")}</th>
            <th>{t("status")}</th>
            <th>{t("actions")}</th>
          </tr>
        );
    }
  };

  const PAYMENT_TYPE_LABEL_KEYS = { ONLINE: "paymentTypeOnline", CASH: "paymentTypeCash", WAIVE_OFF: "paymentTypeWaiveOff" };
  const getPaymentTypeLabel = (item) => {
    const key = PAYMENT_TYPE_LABEL_KEYS[item.paymentType];
    return key ? t(key) : "-";
  };

  const renderTableRow = (item) => {
    // For new/approved/scheduled list items, name+phone+photo come from relativeInfo (flat strings)
    // For upcoming/past session items, patient object may have fullName
    const rel = item.relativeInfo;
    const pat = getPatientFromRequest(item);
    const displayName =
      (typeof pat === "object" && (pat?.fullName || `${pat?.firstName || ""} ${pat?.lastName || ""}`.trim())) ||
      (rel ? `${rel.firstName || ""} ${rel.lastName || ""}`.trim() : "") ||
      "-";
    const displayPhoto =
      (typeof item.patient === "object" && item.patient?.profilePhoto) ||
      item.relativeInfo?.profilePhoto ||
      (typeof pat === "object" && pat?.profilePhoto) ||
      rel?.profilePhoto ||
      null;
    const displayPhone = (() => {
      const src = (typeof pat === "object" && pat?.countryCode) ? pat : rel;
      if (!src) return "-";
      return src.countryCode ? `+${src.countryCode} ${src.phoneNumber}` : src.phoneNumber || src.email || "-";
    })();

    const profileCell = (
      <td>
        <div className="ppl-td-first">
          <div>
            {displayPhoto ? (
              <img
                src={displayPhoto}
                alt="profile-pic"
                className="ppl-td-profile"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
            ) : null}
            <div className="ppl-detials" style={{ display: displayPhoto ? "none" : "flex" }}>
              <img src={Icons.profile} alt="profile-pic" className="ppl-td-profile-none" />
            </div>
          </div>
          <div className="ppl-name-email">
            <section>{displayName}</section>
            <section className="ppl-email">{displayPhone}</section>
          </div>
        </div>
      </td>
    );

    const statusCell = (
      <td className={`ppl-status ${item.status || ""}`}>
        <p>{t(item.status ? item.status : "error")}</p>
      </td>
    );

    switch (activeList) {
      case "past": {
        const nurseObj = nursesToDisplayOption(item.nurse);
        return (
          <tr key={item._id} className="ppl-table-row" onClick={() => handleRowClick(item)}>
            {profileCell}
            <td className="mnr-hn-cell">{getRequestHn(item)}</td>
            <td className="session-dt-cell">{formatSessionDateTime(item.startTime)}</td>
            <td className="mnr-receiving-time-cell">{formatRequestDateTime(item.checkOutTime) || "-"}</td>
            <td>
              <div className="mnr-nurse-pill-readonly">{nurseObj?.label || t("notAssigned")}</div>
            </td>
            <td className="mnr-payment-type-cell">{getPaymentTypeLabel(item)}</td>
            {statusCell}
          </tr>
        );
      }
      case "upcoming": {
        const nurseObj = assignedNurses[item._id] || nursesToDisplayOption(item.nurse);
        const isCheckedIn = item.status === "CHECKED_IN";
        return (
          <tr key={item._id} className="ppl-table-row" onClick={() => handleRowClick(item)}>
            {profileCell}
            <td className="mnr-hn-cell">{getRequestHn(item)}</td>
            <td className="session-dt-cell">{formatSessionDateTime(item.startTime)}</td>
            <td className="mnr-receiving-time-cell">{formatRequestDateTime(item.scheduledAt) || "-"}</td>
            <td>
              <div className="mnr-nurse-cell">
                <div className={`mnr-nurse-pill ${nurseObj ? "assigned" : ""} ${isCheckedIn ? "disabled" : ""}`} onClick={(e) => { if (isCheckedIn) { e.stopPropagation(); return; } handleNurseEdit(e, item); }}>
                  <span>{nurseObj?.label || t("notAssigned")}</span>
                  {!isCheckedIn && <img src={Icons.edit} alt="edit" className="nurse-edit-icon" />}
                </div>
                {!isCheckedIn && (
                  <button
                    type="button"
                    className="mnr-reschedule-icon-btn"
                    title={t("reschedule")}
                    aria-label={t("reschedule")}
                    onClick={(e) => handleOpenReschedule(e, item)}
                  >
                    <img src={Icons.blackCalender} alt="" />
                  </button>
                )}
              </div>
            </td>
            <td className="mnr-payment-type-cell">{getPaymentTypeLabel(item)}</td>
            <td className={`ppl-status ${item.status === "IN_REVIEW" ? "ASSIGNED" : (item.status || "")}`}>
              <p>{t(item.status === "IN_REVIEW" ? "ASSIGNED" : (item.status || "error"))}</p>
            </td>
            <td>
              <button
                type="button"
                className="mnr-delete-icon-btn"
                title={t("deleteSession")}
                aria-label={t("deleteSession")}
                onClick={(e) => openDeleteModal(e, "SESSION", item._id)}
              >
                <img src={Icons.trashIcon} alt="" />
              </button>
            </td>
          </tr>
        );
      }
      case "scheduled":
        return (
          <tr key={item._id} className="ppl-table-row" onClick={() => handleRowClick(item)}>
            {profileCell}
            <td className="mnr-hn-cell">{getRequestHn(item)}</td>
            <td>{item.nursingRequestID || item._id || "-"}</td>
            <td className="mnr-receiving-time-cell">{formatRequestDateTime(item.updatedAt) || "-"}</td>
            <td>{getPaymentTypeLabel(item)}</td>
            {statusCell}
            <td>
              <button type="button" className="mnr-delete-icon-btn" title={t("deleteRequest")} aria-label={t("deleteRequest")} onClick={(e) => openDeleteModal(e, "REQUEST", item._id)}>
                <img src={Icons.trashIcon} alt="" />
              </button>
            </td>
          </tr>
        );
      case "approved":
        return (
          <tr key={item._id} className="ppl-table-row" onClick={() => handleRowClick(item)}>
            {profileCell}
            <td className="mnr-hn-cell">{getRequestHn(item)}</td>
            <td>{item.nursingRequestID || item._id || "-"}</td>
            <td className="mnr-receiving-time-cell">{formatRequestDateTime(item.updatedAt) || "-"}</td>
            {statusCell}
            <td>
              <button type="button" className="mnr-delete-icon-btn" title={t("deleteRequest")} aria-label={t("deleteRequest")} onClick={(e) => openDeleteModal(e, "REQUEST", item._id)}>
                <img src={Icons.trashIcon} alt="" />
              </button>
            </td>
          </tr>
        );
      case "deletedSessions":
        return (
          <tr key={item._id} className="ppl-table-row" onClick={() => handleRowClick(item)}>
            {profileCell}
            <td className="mnr-hn-cell">{getRequestHn(item)}</td>
            <td className="session-dt-cell">{formatSessionDateTime(item.startTime)}</td>
            <td className="mnr-receiving-time-cell">{formatRequestDateTime(cancelledAtById[item._id] ?? item.cancelledAt) || "-"}</td>
            {statusCell}
          </tr>
        );
      case "deletedRequests":
        return (
          <tr key={item._id} className="ppl-table-row" onClick={() => handleRowClick(item)}>
            {profileCell}
            <td className="mnr-hn-cell">{getRequestHn(item)}</td>
            <td>{item.nursingRequestID || item._id || "-"}</td>
            <td className="mnr-receiving-time-cell">{formatRequestDateTime(item.cancelledAt || item.deletedAt || item.cancelAt || item.updatedAt) || "-"}</td>
            {statusCell}
          </tr>
        );
      case "new":
      default:
        return (
          <tr key={item._id} className="ppl-table-row" onClick={() => handleRowClick(item)}>
            {profileCell}
            <td className="mnr-hn-cell">{getRequestHn(item)}</td>
            <td>{item.nursingRequestID || item._id || "-"}</td>
            <td className="mnr-receiving-time-cell">{getRequestReceivingTime(item)}</td>
            {statusCell}
            <td>
              <button type="button" className="mnr-delete-icon-btn" title={t("deleteRequest")} aria-label={t("deleteRequest")} onClick={(e) => openDeleteModal(e, "REQUEST", item._id)}>
                <img src={Icons.trashIcon} alt="" />
              </button>
            </td>
          </tr>
        );
    }
  };

  // Assign Homecare Staff modal — max rows can never exceed the number of Homecare
  // Staff the API says are available for this session; Add More is blocked until
  // the current (last) row has a selection.
  const nurseAssignRowCount = pendingNurseSelections.length;
  const isNurseAssignMaxReached = nurseOptions.length > 0 && nurseAssignRowCount >= nurseOptions.length;
  const isCurrentNurseRowEmpty = !pendingNurseSelections[nurseAssignRowCount - 1];

  return (
    <>
      <div className="nurse-req-container">
        <Widgets.BreadCrumb breadcrumbs={breadcrumbs} paths={paths} />
        <div className="department-heading">{activeList === "upcoming" || activeList === "past" || activeList === "deletedSessions" ? t("sessions") : t("services")}</div>
        {/* Search bar only for non-session tabs */}

        <div className="mnr-search-row">
          <Widgets.SearchBox onSearch={(val) => setSearchTerm(val)} placeholder={t("searchByPatientNameOrHN") || "Search by Patient Name or HN"} value={searchTerm} />
        </div>

        {activeList === "upcoming" && (
          <div style={{ display: "flex", alignItems: "center", gap: "1.2rem", flexWrap: "wrap", margin: "0 0 1.2rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
              <label style={{ fontSize: "1.3rem", color: "var(--grey-text)", fontWeight: 500 }}>{t("fromDate")}:</label>
              <input
                type="date"
                value={appointmentFromDate}
                onChange={(e) => setAppointmentFromDate(e.target.value)}
                style={{ border: "1px solid var(--box-border)", borderRadius: "0.6rem", padding: "0.5rem 0.8rem", fontSize: "1.3rem", background: "var(--main-bg)", color: "var(--text-color)" }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
              <label style={{ fontSize: "1.3rem", color: "var(--grey-text)", fontWeight: 500 }}>{t("toDate")}:</label>
              <input
                type="date"
                value={appointmentToDate}
                min={appointmentFromDate}
                onChange={(e) => setAppointmentToDate(e.target.value)}
                style={{ border: "1px solid var(--box-border)", borderRadius: "0.6rem", padding: "0.5rem 0.8rem", fontSize: "1.3rem", background: "var(--main-bg)", color: "var(--text-color)" }}
              />
            </div>
            {(appointmentFromDate || appointmentToDate) && (
              <button
                type="button"
                onClick={() => { setAppointmentFromDate(""); setAppointmentToDate(""); }}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#e53935", fontSize: "1.3rem", fontWeight: 600 }}
              >
                {t("clear") || "Clear"}
              </button>
            )}
          </div>
        )}

        <div className="ppl-display">
          <section>
            <section>
              {TAB_CONFIG.map(({ key }) => (
                <span key={key} className={`tab ${activeList === key ? "active" : ""}`} onClick={() => handleActiveChange(key)}>
                  {key === activeList ? t(key, { count: activeTabCount }) : t(key + "Label")}
                </span>
              ))}
            </section>
          </section>
          <div className="mnr-right-controls">
                <div className="ppl-icon-container" ref={modalRef}>
              <section className="ppl-icon-filter" onClick={() => setShowFilter(!showFilter)}>
                <img src={Icons.filterIcon} alt="icon-filter" />
              </section>
              {showFilter && (
                <section className="ppl-status-dropdown">
                  <Widgets.StatusDropdown
                    statusTypes={activeTabConfig.filterStatuses || statusTypes}
                    setShowFilter={setShowFilter}
                    selectedStatuses={selectedStatuses}
                    setSelectedStatuses={setSelectedStatuses}
                    onStatusChange={() => {}}
                  />
                </section>
              )}
            </div>
          </div>
        </div>
        <div className="ppl-list-container">
          <div className="ppl-table-body">
            <table className={`ppl-table ${activeTabConfig.session ? "mnr-session-table" : "mnr-request-table"}`}>
              <thead className="ppl-thead">{renderTableHead()}</thead>
              <tbody className="ppl-tbody">{displayList.map((item) => renderTableRow(item))}</tbody>
            </table>
            {totalPages === 0 && (
              <div className="no-record-container ppl">
                <div className="no-record-icon">
                  <img src={Icons.NoData} alt="No Data Found" />
                </div>
                <div className="no-record-text">{t("noRecordFound")}</div>
              </div>
            )}
          </div>
          <div className="department-pagination-list">
            {totalPages > 0 && (
              <>
                <button onClick={() => handlePrevNext("prev")} disabled={listPage === 1} className="department-pagination-prev-list">
                  <img src={Icons.leftArrow} alt="<" />
                </button>
                {renderPagination().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === "number" && handleClick(page)}
                    disabled={page === listPage || page.type === "ellipsis"}
                    className={`department-pagination-btn-list ${page === listPage ? "active-list" : ""} ${page.type === "ellipsis" ? "ellipse" : ""}`}
                  >
                    {typeof page === "number" ? page : <img src={Icons.extraIcon} alt="..." className="department-extra-icon" />}
                  </button>
                ))}
                <button onClick={() => handlePrevNext("next")} disabled={listPage === totalPages} className="department-pagination-next-list">
                  <img src={Icons.rightArrow} alt=">" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {nurseModal.open && !showNurseConfirm && (
        <div className="assign-nurse-modal-overlay" onClick={handleCloseNurseModal}>
          <div className="assign-nurse-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="assign-modal-heading">{isReassign ? (t("reassignNurse") || "Reassign Homecare Staff") : t("assignNurse")}</h3>

            {nurseOptionsLoading ? (
              <div className="assign-modal-body">
                <span className="assign-modal-loading">{t("loading")}…</span>
              </div>
            ) : nurseOptions.length === 0 ? (
              <div className="assign-modal-body">
                <span className="assign-modal-loading">{t("notAvailable")}</span>
              </div>
            ) : (
              <>
                {pendingNurseSelections.map((sel, idx) => {
                  // Options available = all options minus ones selected in other rows
                  const otherSelected = new Set(
                    pendingNurseSelections.filter((s, i) => i !== idx && s).map((s) => s.value)
                  );
                  const availableOptions = nurseOptions.filter((o) => !otherSelected.has(o.value));
                  return (
                    <div key={idx} className="assign-modal-body" style={{ display: "flex", alignItems: "center", gap: "0.8rem" }}>
                      <div className="assign-modal-dropdown" style={{ flex: 1 }}>
                        <Widgets.NewSelectDropdown
                          options={availableOptions}
                          selectedValue={sel}
                          onChange={(opt) => {
                            const updated = [...pendingNurseSelections];
                            updated[idx] = opt;
                            setPendingNurseSelections(updated);
                          }}
                          placeholder={t("selectNurse") || "Select Homecare Staff"}
                          noMargin
                          disabled={nurseModal.sessionItem?.status === "CHECKED_IN"}
                        />
                      </div>
                      {idx > 0 && (
                        <button
                          type="button"
                          onClick={() => setPendingNurseSelections((prev) => prev.filter((_, i) => i !== idx))}
                          style={{ background: "none", border: "none", cursor: "pointer", color: "#e53935", fontSize: "1.6rem", lineHeight: 1, padding: "0.2rem" }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  );
                })}
                {nurseModal.sessionItem?.status !== "CHECKED_IN" && nurseOptions.length > 1 && (
                  <div style={{ padding: "0 1.6rem 0.4rem", textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={() => setPendingNurseSelections((prev) => [...prev, null])}
                      disabled={isNurseAssignMaxReached || isCurrentNurseRowEmpty}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: isNurseAssignMaxReached || isCurrentNurseRowEmpty ? "not-allowed" : "pointer",
                        color: isNurseAssignMaxReached || isCurrentNurseRowEmpty ? "var(--color-light-grey)" : "#009688",
                        fontSize: "1.3rem",
                        fontWeight: 600,
                      }}
                    >
                      + {t("addMoreNurse") || "Add More"}
                    </button>
                    {isNurseAssignMaxReached ? (
                      <span className="assign-modal-error" style={{ textAlign: "right" }}>
                        {t("maximumHomecareStaffReached")}
                      </span>
                    ) : isCurrentNurseRowEmpty ? (
                      <span className="assign-modal-error" style={{ textAlign: "right" }}>
                        {t("completeCurrentAssignment")}
                      </span>
                    ) : null}
                  </div>
                )}
              </>
            )}

            <div className="assign-modal-body assign-modal-field-stack">
              <label className="assign-modal-label" htmlFor="nursing-instructions">
                {t("nursingInstructions")}
              </label>
              <input
                id="nursing-instructions"
                type="text"
                className="assign-modal-text-input"
                value={nursingInstructions}
                onChange={(e) => setNursingInstructions(e.target.value)}
                placeholder={t("enterNursingInstructions")}
              />
            </div>
            {isReassign && (
              <div className="assign-modal-body assign-modal-field-stack">
                <label className="assign-modal-label" htmlFor="reassign-reason">
                  {t("reasonForReassignment") || "Reason for Reassignment"}<span className="asterik">*</span>
                </label>
                <input
                  id="reassign-reason"
                  type="text"
                  className="assign-modal-text-input"
                  value={reassignReason}
                  onChange={(e) => { setReassignReason(e.target.value); if (e.target.value.trim()) setReassignReasonError(false); }}
                  placeholder={t("enterReasonForReassignment") || "Enter reason for reassignment"}
                  style={reassignReasonError ? { border: "1px solid #e53935" } : {}}
                />
                {reassignReasonError && (
                  <span className="assign-modal-error" style={{ color: "#e53935", fontSize: "1.2rem", marginTop: "0.4rem" }}>
                    {t("pleaseEnterReason") || "Please enter a reason for reassignment"}
                  </span>
                )}
              </div>
            )}
            <div className="assign-modal-footer">
              <button className="assign-modal-cancel" onClick={handleCloseNurseModal}>
                {t("cancel")}
              </button>
              <button className="assign-modal-confirm" onClick={handleNurseSaveClick} disabled={!pendingNurseSelections.some(Boolean) || nurseModal.sessionItem?.status === "CHECKED_IN"}>
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNurseConfirm && (
        <div className="assign-nurse-modal-overlay" onClick={() => setShowNurseConfirm(false)}>
          <div className="assign-nurse-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="assign-modal-heading">{t("confirmUpdate") || "Confirm Update"}</h3>
            {/* <p style={{ fontSize: "1.4rem", color: "var(--grey-text)", margin: "1.2rem 0 2rem" }}>{t("confirmNurseUpdate") || "Are you sure you want to update the Homecare Staff?"}</p> */}
            <div className="assign-modal-footer">
              <button className="assign-modal-cancel" onClick={() => setShowNurseConfirm(false)}>
                {t("cancel")}
              </button>
              <button className="assign-modal-confirm" onClick={handleConfirmNurseAssign} disabled={nurseModal.sessionItem?.status === "CHECKED_IN"}>
                {t("confirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      <ChatWidget />

      {rescheduleModal.open && (
        <div className="assign-nurse-modal-overlay" onClick={handleCloseRescheduleModal}>
          <div className="assign-nurse-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="assign-modal-heading">{t("rescheduleSession")}</h3>
            <div className="assign-modal-body assign-modal-field-stack">
              <label className="assign-modal-label">{t("selectDate")}</label>
              <input
                type="date"
                className="assign-modal-text-input"
                value={rescheduleDate}
                min={new Date().toISOString().split("T")[0]}
                onChange={(e) => { setRescheduleDate(e.target.value); setRescheduleErrors((p) => ({ ...p, date: null })); }}
              />
              {rescheduleErrors.date && <span className="assign-modal-error">{rescheduleErrors.date}</span>}
            </div>
            <div className="assign-modal-body assign-modal-field-stack">
              <label className="assign-modal-label">{t("sessionHours")}</label>
              <div className="field-value time-row" style={{ gap: "0.6rem", flexWrap: "wrap" }}>
                <TimePicker
                  value={rescheduleCheckIn}
                  onChange={(val) => { setRescheduleCheckIn(val); setRescheduleErrors((p) => ({ ...p, checkIn: null, timeRange: null })); }}
                  minMinutes={rescheduleDate === new Date().toISOString().split("T")[0] ? new Date().getHours() * 60 + new Date().getMinutes() : null}
                />
                <span className="time-arrow" style={{ fontWeight: 700, alignSelf: "center" }}>→</span>
                <TimePicker
                  value={rescheduleCheckOut}
                  onChange={(val) => { setRescheduleCheckOut(val); setRescheduleErrors((p) => ({ ...p, checkOut: null, timeRange: null })); }}
                />
              </div>
              {rescheduleErrors.checkIn && <span className="assign-modal-error">{rescheduleErrors.checkIn}</span>}
              {rescheduleErrors.checkOut && <span className="assign-modal-error">{rescheduleErrors.checkOut}</span>}
              {rescheduleErrors.timeRange && <span className="assign-modal-error">{rescheduleErrors.timeRange}</span>}
            </div>
            <div className="assign-modal-footer">
              <button className="assign-modal-cancel" onClick={handleCloseRescheduleModal}>{t("cancel")}</button>
              <button className="assign-modal-confirm" onClick={handleRescheduleConfirm}>{t("reschedule")}</button>
            </div>
          </div>
        </div>
      )}

      <Widgets.PaymentReasonModal
        open={deleteModal.open}
        type={deleteModal.kind === "SESSION" ? "DELETE_SESSION" : "DELETE_REQUEST"}
        title={deleteModal.kind === "SESSION" ? t("deleteSession") : t("deleteRequest")}
        loading={deleteLoading}
        onCancel={handleCloseDeleteModal}
        onSubmit={handleDeleteSubmit}
      />
    </>
  );
};

export default ManageNurseRequest;
